import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

export enum SignatureStyle {
  MODERN = "modern",
  CLASSIC = "classic",
  MINIMAL = "minimal",
  CORPORATE = "corporate",
}

export enum LogoPosition {
  LEFT = "left",
  RIGHT = "right",
  TOP = "top",
  BOTTOM = "bottom",
}

export enum IconStyle {
  EMOJIS = "emojis",
  MATERIAL_ICONS = "material_icons",
  NONE = "none",
}

export interface EmailSignatureGeneratorState {
  // User information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  linkedin: string;
  
  // Signature settings
  style: SignatureStyle;
  logoPosition: LogoPosition;
  includeLogo: boolean;
  iconStyle: IconStyle;
  signatureColor: string;
  
  // Logo data
  logoDataUrl: string | null;
  logoFileName: string | null;
  
  // Generated signature
  generatedSignature: string | null;
  
  // UI state
  isGenerating: boolean;
  error: string | null;
}

const initialState: EmailSignatureGeneratorState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  website: "",
  linkedin: "",
  
  style: SignatureStyle.MODERN,
  logoPosition: LogoPosition.LEFT,
  includeLogo: false,
  iconStyle: IconStyle.MATERIAL_ICONS,
  signatureColor: "#252627",
  
  logoDataUrl: null,
  logoFileName: null,
  
  generatedSignature: null,
  
  isGenerating: false,
  error: null,
};

export const EmailSignatureGeneratorSlice = createSlice({
  name: "emailSignatureGenerator",
  initialState,
  reducers: {
    firstNameChanged: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
    },
    
    lastNameChanged: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
    },
    
    emailChanged: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    
    phoneChanged: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },
    
    websiteChanged: (state, action: PayloadAction<string>) => {
      state.website = action.payload;
    },
    
    linkedinChanged: (state, action: PayloadAction<string>) => {
      state.linkedin = action.payload;
    },
    
    styleChanged: (state, action: PayloadAction<SignatureStyle>) => {
      state.style = action.payload;
    },
    
    logoPositionChanged: (state, action: PayloadAction<LogoPosition>) => {
      state.logoPosition = action.payload;
    },
    
    includeLogoChanged: (state, action: PayloadAction<boolean>) => {
      state.includeLogo = action.payload;
      if (!action.payload) {
        state.logoDataUrl = null;
        state.logoFileName = null;
      }
    },
    
    iconStyleChanged: (state, action: PayloadAction<IconStyle>) => {
      state.iconStyle = action.payload;
    },
    
    signatureColorChanged: (state, action: PayloadAction<string>) => {
      state.signatureColor = action.payload;
    },
    
    logoUploaded: (state, action: PayloadAction<{ dataUrl: string; fileName: string }>) => {
      state.logoDataUrl = action.payload.dataUrl;
      state.logoFileName = action.payload.fileName;
      state.includeLogo = true;
    },
    
    logoRemoved: (state) => {
      state.logoDataUrl = null;
      state.logoFileName = null;
      state.includeLogo = false;
    },
    
    signatureGenerated: (state, action: PayloadAction<string>) => {
      state.generatedSignature = action.payload;
      state.isGenerating = false;
      state.error = null;
    },
    
    generationStarted: (state) => {
      state.isGenerating = true;
      state.error = null;
    },
    
    generationFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isGenerating = false;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    resetGenerator: (state) => {
      return { ...initialState };
    },
  },
});

// Utility functions
export function validateEmail(email: string): string | null {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (email && !emailRegex.test(email)) {
    return "Please enter a valid email address";
  }
  return null;
}

export function validatePhone(phone: string): string | null {
  if (!phone) return null;
  
  // Basic phone validation - allows various formats
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  
  if (!phoneRegex.test(cleanPhone)) {
    return "Please enter a valid phone number";
  }
  return null;
}

export function validateUrl(url: string): string | null {
  if (!url) return null;
  
  try {
    new URL(url.startsWith('http') ? url : `https://${url}`);
    return null;
  } catch {
    return "Please enter a valid URL";
  }
}

export function validateLogoFile(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return "Please select a valid image file";
  }
  
  if (file.size > 5 * 1024 * 1024) { // 5MB limit
    return "Logo file size must be less than 5MB";
  }
  
  return null;
}

export function generateSignatureHTML(state: EmailSignatureGeneratorState): string {
  const { firstName, lastName, email, phone, website, linkedin, style, logoPosition, includeLogo, logoDataUrl, iconStyle, signatureColor } = state;
  
  const fullName = `${firstName} ${lastName}`.trim();
  const hasName = fullName.length > 0;
  const hasEmail = email.length > 0;
  const hasPhone = phone.length > 0;
  const hasWebsite = website.length > 0;
  const hasLinkedIn = linkedin.length > 0;
  const hasLogo = includeLogo && logoDataUrl;
  
  if (!hasName && !hasEmail && !hasPhone && !hasWebsite && !hasLinkedIn && !hasLogo) {
    return "";
  }
  
  // Icon mappings with SVG icons for email compatibility
  const getIcon = (type: 'email' | 'phone' | 'website' | 'linkedin') => {
    if (iconStyle === IconStyle.NONE) return '';
    
    const icons = {
      emojis: {
        email: '📧',
        phone: '📞',
        website: '🌐',
        linkedin: '💼'
      },
      material_icons: {
        email: `<svg width="16" height="16" viewBox="0 0 24 24" fill="${signatureColor}" style="vertical-align: middle; margin-right: 4px;"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>`,
        phone: `<svg width="16" height="16" viewBox="0 0 24 24" fill="${signatureColor}" style="vertical-align: middle; margin-right: 4px;"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>`,
        website: `<svg width="16" height="16" viewBox="0 0 24 24" fill="${signatureColor}" style="vertical-align: middle; margin-right: 4px;"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>`,
        linkedin: `<svg width="16" height="16" viewBox="0 0 24 24" fill="${signatureColor}" style="vertical-align: middle; margin-right: 4px;"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>`
      }
    };
    
    if (iconStyle === IconStyle.MATERIAL_ICONS) {
      return icons[iconStyle][type];
    }
    
    return icons[iconStyle][type] + ' ';
  };
  
  // Base styles
  const baseStyles = `
    font-family: Arial, sans-serif;
    font-size: 14px;
    line-height: 1.4;
    color: #333333;
    max-width: 600px;
  `;
  
  // No external fonts needed with SVG icons
  
  // Style-specific configurations with custom color
  const styleConfigs = {
    [SignatureStyle.MODERN]: {
      containerStyle: `${baseStyles} border-left: 4px solid ${signatureColor}; padding-left: 15px;`,
      nameStyle: `font-size: 18px; font-weight: bold; color: ${signatureColor}; margin-bottom: 5px;`,
      contactStyle: "color: #666666; margin: 2px 0;",
      linkStyle: `color: ${signatureColor}; text-decoration: none;`,
    },
    [SignatureStyle.CLASSIC]: {
      containerStyle: `${baseStyles} border-top: 2px solid ${signatureColor}; padding-top: 15px;`,
      nameStyle: `font-size: 16px; font-weight: bold; color: ${signatureColor}; margin-bottom: 8px;`,
      contactStyle: "color: #555555; margin: 3px 0;",
      linkStyle: `color: ${signatureColor}; text-decoration: underline;`,
    },
    [SignatureStyle.MINIMAL]: {
      containerStyle: `${baseStyles} padding: 10px 0;`,
      nameStyle: `font-size: 16px; font-weight: 600; color: ${signatureColor}; margin-bottom: 6px;`,
      contactStyle: "color: #666666; margin: 2px 0; font-size: 13px;",
      linkStyle: `color: ${signatureColor}; text-decoration: none;`,
    },
    [SignatureStyle.CORPORATE]: {
      containerStyle: `${baseStyles} background-color: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 3px solid ${signatureColor};`,
      nameStyle: `font-size: 18px; font-weight: bold; color: ${signatureColor}; margin-bottom: 8px;`,
      contactStyle: "color: #34495e; margin: 4px 0;",
      linkStyle: `color: ${signatureColor}; text-decoration: none; font-weight: 500;`,
    },
  };
  
  const config = styleConfigs[style];
  
  // Count the number of fields provided
  const fieldCount = [hasName, hasEmail, hasPhone, hasWebsite, hasLinkedIn].filter(Boolean).length;
  
  // Generate logo HTML
  let logoHTML = "";
  if (hasLogo) {
    // Determine if we should use dynamic sizing (left/right position with 3+ fields)
    const useDynamicSizing = (logoPosition === LogoPosition.LEFT || logoPosition === LogoPosition.RIGHT) && fieldCount >= 3;
    
    const logoImg = useDynamicSizing 
      ? `<img src="${logoDataUrl}" alt="Logo" style="max-width: 120px; max-height: 100%; object-fit: contain; display: block;">`
      : `<img src="${logoDataUrl}" alt="Logo" style="max-width: 120px; max-height: 60px; display: block; margin: 0 auto 10px auto;">`;
    
    switch (logoPosition) {
      case LogoPosition.LEFT:
        logoHTML = useDynamicSizing 
          ? `<div style="display: flex; align-items: center; justify-content: center;">${logoImg}</div>`
          : `<div style="float: left; margin-right: 15px; margin-bottom: 10px;">${logoImg}</div>`;
        break;
      case LogoPosition.RIGHT:
        logoHTML = useDynamicSizing 
          ? `<div style="display: flex; align-items: center; justify-content: center;">${logoImg}</div>`
          : `<div style="float: right; margin-left: 15px; margin-bottom: 10px;">${logoImg}</div>`;
        break;
      case LogoPosition.TOP:
        logoHTML = `<div style="text-align: center; margin-bottom: 15px;">${logoImg}</div>`;
        break;
      case LogoPosition.BOTTOM:
        logoHTML = `<div style="text-align: center; margin-top: 15px;">${logoImg}</div>`;
        break;
    }
  }
  
  // Generate content HTML
  let contentHTML = "";
  
  if (hasName) {
    contentHTML += `<div style="${config.nameStyle}">${fullName}</div>`;
  }
  
  if (hasEmail) {
    contentHTML += `<div style="${config.contactStyle}">${getIcon('email')}<a href="mailto:${email}" style="${config.linkStyle}">${email}</a></div>`;
  }
  
  if (hasPhone) {
    contentHTML += `<div style="${config.contactStyle}">${getIcon('phone')}<a href="tel:${phone}" style="${config.linkStyle}">${phone}</a></div>`;
  }
  
  if (hasWebsite) {
    const websiteUrl = website.startsWith('http') ? website : `https://${website}`;
    const displayUrl = website.replace(/^https?:\/\//, '');
    contentHTML += `<div style="${config.contactStyle}">${getIcon('website')}<a href="${websiteUrl}" style="${config.linkStyle}">${displayUrl}</a></div>`;
  }
  
  if (hasLinkedIn) {
    const linkedinUrl = linkedin.startsWith('http') ? linkedin : `https://${linkedin}`;
    const displayUrl = linkedin.replace(/^https?:\/\//, '');
    contentHTML += `<div style="${config.contactStyle}">${getIcon('linkedin')}<a href="${linkedinUrl}" style="${config.linkStyle}">${displayUrl}</a></div>`;
  }
  
  // Combine logo and content
  let signatureHTML = "";
  
  // Determine if we should use dynamic sizing for the content container
  const useDynamicSizing = (logoPosition === LogoPosition.LEFT || logoPosition === LogoPosition.RIGHT) && fieldCount >= 3 && hasLogo;
  
  if (logoPosition === LogoPosition.TOP && hasLogo) {
    signatureHTML = `
      <div style="${config.containerStyle}">
        ${logoHTML}
        <div style="overflow: hidden;">
          ${contentHTML}
        </div>
      </div>
    `;
  } else if (logoPosition === LogoPosition.BOTTOM && hasLogo) {
    signatureHTML = `
      <div style="${config.containerStyle}">
        <div style="overflow: hidden;">
          ${contentHTML}
        </div>
        ${logoHTML}
      </div>
    `;
  } else if (hasLogo) {
    if (useDynamicSizing) {
      // Use table layout for proper height matching
      signatureHTML = `
        <div style="${config.containerStyle}">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              ${logoPosition === LogoPosition.LEFT ? `<td style="vertical-align: middle; width: 135px; padding-right: 15px;">${logoHTML.replace(/float: [^;]+;/, '').replace(/margin-[^:]+: [^;]+;/, '')}</td>` : ''}
              <td style="vertical-align: middle;">
                <div style="overflow: hidden;">
                  ${contentHTML}
                </div>
              </td>
              ${logoPosition === LogoPosition.RIGHT ? `<td style="vertical-align: middle; width: 135px; padding-left: 15px;">${logoHTML.replace(/float: [^;]+;/, '').replace(/margin-[^:]+: [^;]+;/, '')}</td>` : ''}
            </tr>
          </table>
        </div>
      `;
    } else {
      // Use float layout for standard sizing
      signatureHTML = `
        <div style="${config.containerStyle}">
          ${logoHTML}
          <div style="overflow: hidden;">
            ${contentHTML}
          </div>
          <div style="clear: both;"></div>
        </div>
      `;
    }
  } else {
    signatureHTML = `
      <div style="${config.containerStyle}">
        ${contentHTML}
      </div>
    `;
  }
  
  return signatureHTML.trim();
}

export const {
  firstNameChanged,
  lastNameChanged,
  emailChanged,
  phoneChanged,
  websiteChanged,
  linkedinChanged,
  styleChanged,
  logoPositionChanged,
  includeLogoChanged,
  iconStyleChanged,
  signatureColorChanged,
  logoUploaded,
  logoRemoved,
  signatureGenerated,
  generationStarted,
  generationFailed,
  clearError,
  resetGenerator,
} = EmailSignatureGeneratorSlice.actions;

export const selectState = (state: RootState) => state.emailSignatureGenerator;

export default EmailSignatureGeneratorSlice.reducer;
